import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import api from "../api/axios";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading
  const versionRef = useRef(null);

  useEffect(() => {
    api.get("/auth/me")
      .then((res) => {
        setUser(res.data);
        versionRef.current = res.data?.rbac?.role_version ?? null;
        Sentry.setUser({ id: res.data?.id, email: res.data?.email });
      })
      .catch(() => setUser(null));
  }, []);

  // C13 — permission staleness: poll /auth/me; if the role version changed
  // (an admin edited this user's role), refetch so nav/buttons update live.
  useEffect(() => {
    if (!user) return undefined;
    const id = setInterval(async () => {
      try {
        const res = await api.get("/auth/me");
        const v = res.data?.rbac?.role_version ?? null;
        if (v !== versionRef.current) {
          versionRef.current = v;
          setUser(res.data);
        }
      } catch { /* ignore transient errors */ }
    }, 60000);
    return () => clearInterval(id);
  }, [user]);

  const login = async (email, password, captchaAnswer, captchaToken, turnstileToken) => {
    const res = await api.post("/auth/login", {
      email,
      password,
      captcha_answer: captchaAnswer,
      captcha_token: captchaToken,
      turnstile_token: turnstileToken,
    });
    if (res.data?.twofa_required || res.data?.force_reset_required) return res.data; // don't set session yet
    setUser(res.data);
    versionRef.current = res.data?.rbac?.role_version ?? null;
    Sentry.setUser({ id: res.data?.id, email: res.data?.email });
    return res.data;
  };

  const verify2fa = async (challenge_id, otp) => {
    const res = await api.post("/auth/verify-2fa", { challenge_id, otp });
    setUser(res.data);
    versionRef.current = res.data?.rbac?.role_version ?? null;
    Sentry.setUser({ id: res.data?.id, email: res.data?.email });
    return res.data;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    Sentry.setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      versionRef.current = res.data?.rbac?.role_version ?? null;
      return res.data;
    } catch {
      setUser(null);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, verify2fa, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// ─── Permission helpers (Phase A — advisory; enforcement lands later) ───
export function can(user, resource, action) {
  const r = user?.rbac;
  if (!r) return false;
  if (r.is_super_admin) return true;
  return (r.resources?.[resource] || []).includes(action);
}

export function canNamed(user, name) {
  const r = user?.rbac;
  if (!r) return false;
  if (r.is_super_admin) return true;
  return (r.named || []).includes(name);
}
